import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { opsApi, apiError, FONT_HEAD } from './opsApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Landmark, ShieldCheck, MapPinned, Cpu, IndianRupee, ArrowLeft, ScanLine } from 'lucide-react';

const GREEN = '#34A853';
const GREEN_LIGHT = '#E8F5E9';
const INK = '#0F172A';

export default function OpsLogin() {
  const navigate = useNavigate();
  const [step, setStep] = useState('password'); // password | 2fa | 2fa_setup
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [preToken, setPreToken] = useState('');
  const [setupInfo, setSetupInfo] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submitPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await opsApi.post('/auth/login', { email, password });
      setPreToken(data.pre_token);
      if (data.step === '2fa_setup') {
        setSetupInfo({ qr: data.qr_png, secret: data.secret });
        setStep('2fa_setup');
      } else {
        setStep('2fa');
      }
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const submitTestMode = async () => {
    setError('');
    if (!email || !password) {
      setError('Enter email and password first, then click Test Mode.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await opsApi.post('/auth/login', { email, password, test_mode: true });
      if (data.step === 'done' && data.token) {
        localStorage.setItem('desky_ops_token', data.token);
        navigate('/sales');
      } else {
        setError('Test mode unavailable for this account.');
      }
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await opsApi.post('/auth/2fa/verify', { pre_token: preToken, code });
      localStorage.setItem('desky_ops_token', data.token);
      navigate('/sales');
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Form panel — clean white, on the left */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2.5 mb-10">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: GREEN }}>
              <Landmark className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-lg font-bold tracking-tight text-slate-900" style={FONT_HEAD}>Desk Ops</div>
              <div className="text-[11px] text-slate-500">National Education Operations</div>
            </div>
          </div>

          {step === 'password' && (
            <>
              <h2 className="text-3xl font-semibold tracking-tight mb-2" style={{ ...FONT_HEAD, color: INK }}>Operations sign in</h2>
              <p className="text-sm text-slate-500 mb-8">Desk-side accounts only. 2-factor authentication is mandatory.</p>
              <form onSubmit={submitPassword} className="space-y-5">
                <div>
                  <Label htmlFor="ops-email" className="text-sm font-medium text-slate-700">Work email</Label>
                  <Input id="ops-email" data-testid="ops-login-email" type="email" required value={email}
                    onChange={(e) => setEmail(e.target.value)} placeholder="you@desky.app"
                    className="mt-1.5 h-11 border-slate-300 focus-visible:ring-2 focus-visible:ring-green-200 focus-visible:border-[#34A853]" />
                </div>
                <div>
                  <Label htmlFor="ops-password" className="text-sm font-medium text-slate-700">Password</Label>
                  <Input id="ops-password" data-testid="ops-login-password" type="password" required value={password}
                    onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                    className="mt-1.5 h-11 border-slate-300 focus-visible:ring-2 focus-visible:ring-green-200 focus-visible:border-[#34A853]" />
                </div>
                {error && <div data-testid="ops-login-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>}
                <Button data-testid="ops-login-submit" type="submit" disabled={loading}
                  className="w-full h-11 text-white font-medium rounded-lg border-0 hover:opacity-95 transition-opacity"
                  style={{ backgroundColor: GREEN }}>
                  {loading ? 'Checking…' : 'Continue'}
                </Button>
                <div className="pt-1">
                  <button
                    type="button"
                    onClick={submitTestMode}
                    disabled={loading}
                    data-testid="ops-login-test-mode"
                    className="w-full h-9 text-xs font-medium rounded-md border border-dashed border-slate-300 bg-white text-slate-600 hover:bg-slate-50 hover:border-slate-400 transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
                    title="Dev/QA only — bypasses 2FA. Logged in audit trail."
                  >
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />
                    Test Mode · Skip 2FA
                  </button>
                </div>
              </form>
            </>
          )}

          {step === '2fa_setup' && setupInfo && (
            <>
              <h2 className="text-2xl font-semibold tracking-tight mb-2 flex items-center gap-2" style={{ ...FONT_HEAD, color: INK }}>
                <ScanLine className="w-5 h-5" style={{ color: GREEN }} /> Set up two-factor authentication
              </h2>
              <p className="text-sm text-slate-500 mb-6">2FA is mandatory. Scan this QR with Google Authenticator or Authy, then enter the 6-digit code.</p>
              <div className="flex justify-center mb-3">
                <img src={setupInfo.qr} alt="TOTP QR" data-testid="ops-2fa-qr" className="w-44 h-44 rounded-lg border border-slate-200" />
              </div>
              <p className="text-[11px] text-slate-400 text-center mb-4 font-mono break-all">Manual key: <span data-testid="ops-2fa-secret">{setupInfo.secret}</span></p>
              <form onSubmit={submitCode} className="space-y-3">
                <Input data-testid="ops-2fa-code" inputMode="numeric" maxLength={6} required value={code}
                  onChange={(e) => setCode(e.target.value)} placeholder="6-digit code"
                  className="h-11 text-center text-lg tracking-[0.4em] font-mono border-slate-300" />
                {error && <div data-testid="ops-2fa-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>}
                <Button data-testid="ops-2fa-submit" type="submit" disabled={loading || code.length !== 6}
                  className="w-full h-11 text-white font-medium rounded-lg border-0 hover:opacity-95 transition-opacity"
                  style={{ backgroundColor: GREEN }}>
                  {loading ? 'Verifying…' : 'Enable 2FA & sign in'}
                </Button>
              </form>
            </>
          )}

          {step === '2fa' && (
            <>
              <h2 className="text-2xl font-semibold tracking-tight mb-2 flex items-center gap-2" style={{ ...FONT_HEAD, color: INK }}>
                <ShieldCheck className="w-5 h-5" style={{ color: GREEN }} /> Two-factor verification
              </h2>
              <p className="text-sm text-slate-500 mb-8">Enter the 6-digit code from your authenticator app.</p>
              <form onSubmit={submitCode} className="space-y-3">
                <Input data-testid="ops-2fa-code" inputMode="numeric" maxLength={6} required value={code} autoFocus
                  onChange={(e) => setCode(e.target.value)} placeholder="000000"
                  className="h-12 text-center text-xl tracking-[0.4em] font-mono border-slate-300" />
                {error && <div data-testid="ops-2fa-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>}
                <Button data-testid="ops-2fa-submit" type="submit" disabled={loading || code.length !== 6}
                  className="w-full h-11 text-white font-medium rounded-lg border-0 hover:opacity-95 transition-opacity"
                  style={{ backgroundColor: GREEN }}>
                  {loading ? 'Verifying…' : 'Verify & sign in'}
                </Button>
                <button type="button" onClick={() => { setStep('password'); setCode(''); setError(''); }}
                  className="w-full text-xs text-slate-400 hover:text-slate-600 flex items-center justify-center gap-1 pt-1">
                  <ArrowLeft className="w-3 h-3" /> Back to password
                </button>
              </form>
            </>
          )}

          <p className="text-xs text-slate-400 mt-10">Restricted access · Desk operations, sales &amp; management team only</p>
        </div>
      </div>

      {/* Light-green soft panel — right side */}
      <div
        className="hidden lg:flex w-[46%] flex-col justify-between p-12 relative overflow-hidden"
        style={{ backgroundColor: GREEN_LIGHT }}
      >
        {/* Decorative circles — keeps the panel airy */}
        <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full opacity-30" style={{ backgroundColor: '#C8EAD0' }} />
        <div className="absolute bottom-10 right-20 w-44 h-44 rounded-full opacity-25" style={{ backgroundColor: '#A7E0B5' }} />
        <div className="absolute top-1/3 -left-10 w-32 h-32 rounded-full opacity-20" style={{ backgroundColor: '#C8EAD0' }} />

        <div className="relative flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider bg-white/80 backdrop-blur" style={{ color: GREEN }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: GREEN }} /> Enterprise Operations
          </span>
        </div>

        <div className="relative">
          <h1 className="text-[44px] leading-[1.05] font-semibold tracking-tight mb-4" style={{ ...FONT_HEAD, color: INK }}>
            Every school in India.
            <br />
            <span style={{ color: GREEN }}>One command center.</span>
          </h1>
          <p className="text-base text-slate-600 mb-10 max-w-md">
            The national operations platform powering Desky — from zone leadership to the rep on the ground.
          </p>

          <div className="space-y-3 max-w-md">
            {[
              { icon: MapPinned, text: 'Geographic scoping enforced at the data layer — zone → state → city' },
              { icon: IndianRupee, text: 'Institution licensing, tiered billing & revenue rollups to national level' },
              { icon: Cpu, text: 'Hardware kit fleet — inventory, allocation & device health' },
              { icon: ShieldCheck, text: 'Mandatory 2FA, hierarchical approvals & full audit trail' },
            ].map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-3 text-sm text-slate-700 bg-white/70 backdrop-blur rounded-xl px-4 py-3 border border-white">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: GREEN }}>
                  <Icon className="w-4 h-4 text-white" />
                </div>
                {text}
              </div>
            ))}
          </div>
        </div>

        <div className="relative flex items-center justify-between text-xs text-slate-500">
          <span>© {new Date().getFullYear()} Desky · Internal portal</span>
          <span className="font-medium" style={{ color: GREEN }}>desky.app</span>
        </div>
      </div>
    </div>
  );
}
